<script setup>
/**
 * @todo When the legacy theme is removed: delete the legacyProps prop, the useTheme import,
 * and collapse effectiveProps to { ...base, unelevated: true, ...attrs }.
 */

import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';
import HrSkeleton from './HrSkeleton.vue';

/**
 * Base button primitive wrapping q-btn with design-system variant and size tokens.
 *
 * @status internal
 * @group actions
 * @a11y Native button element; activates on Enter and Space. Renders as a router-link or anchor
 * when `to`/`href` is set.
 * @see https://quasar.dev/vue-components/button Quasar q-btn API
 */
/*
 * Attrs are spread into effectiveProps by hand, so automatic fallthrough would apply each one twice.
 * Twice is harmless for most of them and not for the click listener, which an unavailable button
 * has to be able to withhold.
 */
defineOptions({ name: 'HrBtn', inheritAttrs: false });

// Every way q-btn can be given somewhere to go, and so every way it can be activated by the browser.
const DESTINATION_PROPS = ['to', 'href', 'target', 'replace'];

const props = defineProps({
  /**
   * Visual emphasis variant derived from the M3 hierarchy.
   * @values filled, tonal, elevated, outlined, text
   */
  variant: {
    type: String,
    default: 'filled',
    validator: (v) => ['filled', 'tonal', 'elevated', 'outlined', 'text'].includes(v),
  },
  /**
   * Size of the button, which affects padding and font size.
   * @values xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl'].includes(v),
  },
  /**
   * Button text label. When null the default slot provides the content.
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Icon displayed to the left of the label.
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Icon displayed to the right of the label.
   */
  iconRight: {
    type: String,
    default: null,
  },
  /**
   * Prevents interaction and applies a dimmed appearance.
   */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * The action cannot be taken right now and the button says why, in a tooltip in its slot. It
   * reads as disabled and does nothing, but stays hoverable and focusable so the reason is
   * reachable: a native disabled button receives no mouse events, so its tooltip never opens.
   * Where both are set, `disable` wins.
   */
  unavailable: {
    type: Boolean,
    default: false,
  },
  /**
   * Shows a loading spinner in place of the label.
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * Native button type.
   * @values button, submit, reset
   */
  type: {
    type: String,
    default: 'button',
    validator: (v) => ['button', 'submit', 'reset'].includes(v),
  },
  /**
   * Switches the color role from primary to error. Use for destructive actions.
   */
  destructive: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders a pill-shaped skeleton placeholder instead of the button.
   * Use when the button's action or label is not yet available.
   */
  pending: {
    type: Boolean,
    default: false,
  },
  /**
   * Vue Router route location. When set the button renders as a router-link.
   */
  to: {
    type: [String, Object],
    default: null,
  },
  /**
   * Replace the current history entry instead of pushing a new one.
   */
  replace: {
    type: Boolean,
    default: false,
  },
  /**
   * Native anchor href. Use for external links; prefer `to` for in-app navigation.
   */
  href: {
    type: String,
    default: null,
  },
  /**
   * Native anchor target (e.g. '_blank'). Only meaningful when `href` is set.
   */
  target: {
    type: String,
    default: null,
  },
  /**
   * Quasar props forwarded to q-btn when the legacy theme is active. Ignored entirely under the
   * design-system theme. Use this to pass visual/behavioral Quasar props (e.g. flat, color,
   * dense) that have no equivalent in the new design system.
   *
   * Note: this is distinct from $attrs. legacyProps carries Quasar-specific appearance props and
   * is silenced in the new theme. $attrs carries event listeners, aria-* and data-* attributes,
   * and is always forwarded to q-btn regardless of theme. Both channels are needed and serve
   * different lifecycles — legacyProps disappears when the legacy theme is removed, $attrs stays.
   *
   * IMPORTANT: In the new (themed) mode $attrs is forwarded unfiltered to q-btn. Do NOT pass
   * Quasar visual/behavioral props (flat, color, dense, outline, rounded, …) as plain attributes
   * to HrBtn in a themed context — use legacyProps instead so they are automatically suppressed
   * when the legacy theme is removed.
   *
   * Semantic wrappers inject their own Quasar defaults here and may expose this prop so callers
   * can add further overrides. When a wrapper exposes this prop, it must bind `:legacy-props`
   * after `v-bind="$props"` so the merged defaults survive.
   *
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const isUnavailable = computed(() => props.unavailable && !props.disable);

// `@click.once` and `@click.capture` arrive as `onClickOnce` and `onClickCapture`, not `onClick`.
const CLICK_LISTENER = /^onClick(Once|Capture|Passive)*$/;

const attrsWithoutClick = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([key]) => !CLICK_LISTENER.test(key)),
));

const classes = computed(() => [
  'hr-btn',
  `hr-btn--${props.variant}`,
  `hr-btn--size-${props.size}`,
  props.destructive && 'hr-btn--destructive',
]);

// Two prop channels reach q-btn:
//   legacyProps — Quasar visual props supplied by semantic wrappers; omitted in new theme.
//   attrs       — event listeners, aria-*, data-*, etc.; always forwarded in both themes.
const effectiveProps = computed(() => {
  const forwardedAttrs = isUnavailable.value ? attrsWithoutClick.value : attrs;
  const base = {
    ...(props.label !== null && { label: props.label }),
    ...(props.icon !== null && { icon: props.icon }),
    ...(props.iconRight !== null && { iconRight: props.iconRight }),
    disable: props.disable,
    loading: props.loading,
    type: props.type,
    ...(props.to !== null && { to: props.to }),
    ...(props.replace && { replace: true }),
    ...(props.href !== null && { href: props.href }),
    ...(props.target !== null && { target: props.target }),
    ...(props.target === '_blank' && attrs.rel == null && { rel: 'noopener noreferrer' }),
    // A submit button submits its form natively, click listener or not.
    ...(isUnavailable.value && { 'aria-disabled': 'true', ripple: false, type: 'button' }),
  };

  const merged = hasTheme.value
    ? { ...base, unelevated: true, ...forwardedAttrs }
    : { ...base, size: props.size, ...props.legacyProps, ...forwardedAttrs };

  if (!isUnavailable.value) {
    return merged;
  }
  // Taken off last, as in HrIconBtn: legacyProps could otherwise put a destination back.
  return Object.fromEntries(
    Object.entries(merged).filter(([key]) => !DESTINATION_PROPS.includes(key)),
  );
});
</script>
<template>
  <hr-skeleton
    v-if="props.pending"
    type="pill"
    :height="`var(--app-comp-button-container-height-${props.size})`"
  />
  <q-btn
    v-else
    v-bind="effectiveProps"
    :class="[hasTheme ? classes : undefined, isUnavailable && 'hr-btn--unavailable']"
  >
    <!-- @slot Button label or arbitrary content; forwarded to the underlying q-btn. -->
    <slot />
  </q-btn>
</template>
<style lang="scss" scoped>

/* Only applies to light/dark themes */
.hr-btn {
  border-radius: var(--app-shape-corner-full);
  text-transform: none;
}

.hr-btn--filled {
  background: var(--app-primary);
  color: var(--app-on-primary);
}

.hr-btn--tonal {
  background: var(--app-secondary-container);
  color: var(--app-on-secondary-container);
}

.hr-btn--elevated {
  background: var(--app-surface-container-low);
  color: var(--app-primary);
  box-shadow: var(--app-elevation-1);
}

.hr-btn--outlined {
  background: transparent;
  color: var(--_btn-color, var(--app-primary));
  border: 1px solid var(--_btn-border, var(--app-outline));
}

.hr-btn--text {
  background: transparent;
  color: var(--_btn-color, var(--app-primary));
}

.hr-btn--size-xs {
  min-height: var(--app-comp-button-container-height-xs);
  padding: 0 var(--app-comp-button-container-padding-xs);
  font-size: var(--app-comp-button-label-text-size-xs);
}
.hr-btn--size-sm {
  min-height: var(--app-comp-button-container-height-sm);
  padding: 0 var(--app-comp-button-container-padding-sm);
  font-size: var(--app-comp-button-label-text-size-sm);
}
.hr-btn--size-md {
  min-height: var(--app-comp-button-container-height-md);
  padding: 0 var(--app-comp-button-container-padding-md);
  font-size: var(--app-comp-button-label-text-size-md);
}
.hr-btn--size-lg {
  min-height: var(--app-comp-button-container-height-lg);
  padding: 0 var(--app-comp-button-container-padding-lg);
  font-size: var(--app-comp-button-label-text-size-lg);
}
.hr-btn--size-xl {
  min-height: var(--app-comp-button-container-height-xl);
  padding: 0 var(--app-comp-button-container-padding-xl);
  font-size: var(--app-comp-button-label-text-size-xl);
}

.hr-btn.hr-btn--destructive {
  color: var(--app-error);
}

.hr-btn.hr-btn--destructive.hr-btn--filled {
  background: var(--app-error);
  color: var(--app-on-error);
}

/* State layer — Quasar's q-focus-helper is disabled in favor of this */
.hr-btn :deep(.q-focus-helper) { display: none; }

.hr-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms linear;
}

.hr-btn--filled::before          { background: var(--app-on-primary); }
.hr-btn--tonal::before           { background: var(--app-on-secondary-container); }
.hr-btn--elevated::before        { background: var(--app-primary); }
.hr-btn--outlined::before,
.hr-btn--text::before            { background: var(--_btn-color, var(--app-primary)); }

.hr-btn:hover::before            { opacity: 0.08; }
.hr-btn:focus-visible::before    { opacity: 0.12; }

/*
 * Quasar's only focus indication for a button is the q-focus-helper hidden above, and
 * .q-btn carries outline: 0, so without this a keyboard user tabbing through a dialog's
 * actions sees nothing move. The ring is drawn as well as the state layer, because the
 * layer alone is a tint a filled button barely shows.
 */
.hr-btn:focus-visible {
  outline: 2px solid var(--app-primary);
  outline-offset: 2px;
}

.hr-btn--destructive:focus-visible {
  outline-color: var(--app-error);
}

.hr-btn--destructive::before                        { background: var(--app-error); }
.hr-btn--destructive.hr-btn--filled::before         { background: var(--app-on-error); }

/*
 * Quasar's own disabled look, applied by hand to an unavailable button, which is not natively
 * disabled. Not scoped to a theme: a control that cannot be used has to look that way in legacy too.
 */
.hr-btn--unavailable {
  opacity: 0.6;
  cursor: not-allowed;
}

.hr-btn.disabled,
.hr-btn.hr-btn--unavailable {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-btn--filled.disabled,
.hr-btn--tonal.disabled,
.hr-btn--elevated.disabled,
.hr-btn--filled.hr-btn--unavailable,
.hr-btn--tonal.hr-btn--unavailable,
.hr-btn--elevated.hr-btn--unavailable {
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

.hr-btn--outlined.disabled,
.hr-btn--outlined.hr-btn--unavailable {
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}
</style>
